import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import AgoraService from './AgoraService';
import css from './VideoCallPanel.module.css';

/**
 * VideoCallPanel — embed a real-time video call within a transaction page.
 *
 * Requires:
 *   REACT_APP_AGORA_APP_ID  — Agora App ID
 *   Server: /api/agora/call-data?txId=&role=  — returns Agora token
 *
 * @param {string}  props.transactionId
 * @param {string}  props.role             — 'provider' | 'customer'
 * @param {string}  [props.className]
 */
const VideoCallPanel = ({ transactionId, role, className }) => {
  const intl = useIntl();
  const [status, setStatus] = useState('idle'); // idle | loading | active | ended | error
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState([]);

  const agoraRef = useRef(null);
  const localVideoRef = useRef(null);

  const getAgoraService = () => {
    if (!agoraRef.current) agoraRef.current = new AgoraService();
    return agoraRef.current;
  };

  const handleJoin = useCallback(async () => {
    setStatus('loading');
    setErrorMsg(null);

    try {
      // Fetch token from server
      const res = await fetch(`/api/agora/call-data?txId=${transactionId}&role=${role}`);
      if (!res.ok) throw new Error('Failed to get call data');
      const { agoraToken, channelName, appId } = await res.json();

      // Load Agora SDK and join channel
      const svc = getAgoraService();
      await svc.load();
      const client = svc.createClient();

      // Listen for remote users
      client.on('user-published', async (user, mediaType) => {
        await client.subscribe(user, mediaType);
        if (mediaType === 'video') {
          setRemoteUsers(prev => [...prev.filter(u => u.uid !== user.uid), user]);
          user.videoTrack?.play(`remote-video-${user.uid}`);
        }
        if (mediaType === 'audio') {
          user.audioTrack?.play();
        }
      });

      client.on('user-unpublished', user => {
        setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
      });

      client.on('user-left', user => {
        setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
      });

      const { videoTrack } = await svc.joinChannel(
        appId,
        channelName,
        agoraToken.token,
        agoraToken.account
      );

      // Play local video
      if (videoTrack && localVideoRef.current) {
        videoTrack.play(localVideoRef.current);
      }

      setStatus('active');
    } catch (err) {
      console.error('[agoraVideo] Join error:', err);
      setErrorMsg(err.message);
      setStatus('error');
    }
  }, [transactionId, role]);

  const handleLeave = useCallback(async () => {
    try {
      await getAgoraService().leaveChannel();
    } catch (e) {}
    setRemoteUsers([]);
    setStatus('ended');
  }, []);

  const handleToggleMute = async () => {
    const next = !isMuted;
    await getAgoraService().setMuted(next);
    setIsMuted(next);
  };

  const handleToggleVideo = async () => {
    const next = !isVideoOff;
    await getAgoraService().setVideoEnabled(!next);
    setIsVideoOff(next);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (status === 'active') {
        getAgoraService().leaveChannel().catch(() => {});
      }
    };
  }, [status]); // eslint-disable-line

  return (
    <div className={classNames(css.root, className)}>
      {status === 'idle' && (
        <div className={css.joinScreen}>
          <p className={css.joinText}>
            <FormattedMessage id="VideoCallPanel.readyToJoin" />
          </p>
          <button type="button" onClick={handleJoin} className={css.joinBtn}>
            <FormattedMessage id="VideoCallPanel.joinCall" />
          </button>
        </div>
      )}

      {status === 'loading' && (
        <div className={css.loading}>
          <FormattedMessage id="VideoCallPanel.connecting" />
        </div>
      )}

      {status === 'active' && (
        <div className={css.callScreen}>
          {/* Remote videos */}
          <div className={css.remoteVideos}>
            {remoteUsers.length === 0 ? (
              <div className={css.waitingForOthers}>
                <FormattedMessage id="VideoCallPanel.waitingForOthers" />
              </div>
            ) : (
              remoteUsers.map(user => (
                <div
                  key={user.uid}
                  id={`remote-video-${user.uid}`}
                  className={css.remoteVideo}
                />
              ))
            )}
          </div>

          {/* Local video (picture-in-picture) */}
          <div ref={localVideoRef} className={css.localVideo} />

          {/* Controls */}
          <div className={css.controls}>
            <button
              type="button"
              onClick={handleToggleMute}
              className={classNames(css.controlBtn, { [css.controlBtnOff]: isMuted })}
            >
              {isMuted
                ? intl.formatMessage({ id: 'VideoCallPanel.unmute' })
                : intl.formatMessage({ id: 'VideoCallPanel.mute' })}
            </button>
            <button
              type="button"
              onClick={handleToggleVideo}
              className={classNames(css.controlBtn, { [css.controlBtnOff]: isVideoOff })}
            >
              {isVideoOff
                ? intl.formatMessage({ id: 'VideoCallPanel.videoOn' })
                : intl.formatMessage({ id: 'VideoCallPanel.videoOff' })}
            </button>
            <button
              type="button"
              onClick={handleLeave}
              className={classNames(css.controlBtn, css.hangupBtn)}
            >
              <FormattedMessage id="VideoCallPanel.hangUp" />
            </button>
          </div>
        </div>
      )}

      {status === 'ended' && (
        <div className={css.ended}>
          <FormattedMessage id="VideoCallPanel.callEnded" />
          <button type="button" onClick={() => setStatus('idle')} className={css.joinBtn}>
            <FormattedMessage id="VideoCallPanel.rejoin" />
          </button>
        </div>
      )}

      {status === 'error' && (
        <div className={css.errorScreen}>
          <p>{errorMsg || intl.formatMessage({ id: 'VideoCallPanel.error' })}</p>
          <button type="button" onClick={() => setStatus('idle')} className={css.joinBtn}>
            <FormattedMessage id="VideoCallPanel.retry" />
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoCallPanel;
